import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Button, Space, message } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import { createSlice, configureStore, createAsyncThunk } from "@reduxjs/toolkit";
import { Provider, useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { base_url } from "../utils/baseUrl"; // Import base_url
import * as XLSX from "xlsx";

// Async Thunk for fetching users
const getUsers = createAsyncThunk("customers/getUsers", async () => {
  // Use base_url to make the API call
  const response = await axios.get(`${base_url}/api/auth/users`);
  return response.data;
});

// Redux Slice for customers
const customerSlice = createSlice({
  name: "customer",
  initialState: {
    customers: [],
    status: null,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.customers = action.payload;
      })
      .addCase(getUsers.rejected, (state) => {
        state.status = "failed";
      });
  },
});

// Redux Store
const store = configureStore({
  reducer: {
    customer: customerSlice.reducer,
  },
});



const columns = [
  {
    title: "SNo",
    dataIndex: "key",
  },
  {
    title: "Name",
    dataIndex: "name",
    sorter: (a, b) => a.name.length - b.name.length,
  },
  {
    title: "Email",
    dataIndex: "email",
  },
  {
    title: "Phone",
    dataIndex: "phone",
  },
  {
    title: "Address",
    dataIndex: "address",
  },
  {
    title: "Country",
    dataIndex: "country",
  },
];



const Customers = () => {
  const [isValid, setIsValid] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch users on component mount
  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  const adminToken = localStorage.getItem("adminToken");
  console.log("Admin Token:", adminToken);

  useEffect(() => {
    const adminToken = localStorage.getItem("adminToken");
    if (!adminToken) {
      navigate("/");
    }
  }, [navigate]);

  const customerState = useSelector((state) => state.customer.customers);
  const data = [];

  for (let i = 0; i < customerState.length; i++) {
    if (customerState[i].role !== "admin") {
      // Concatenate the full address
      const fullAddress = [
        customerState[i].address,
        customerState[i].city,
        customerState[i].state,
        customerState[i].postal_code,
        customerState[i].country,
      ]
        .filter(Boolean) // Remove any empty values
        .join(", ");

      data.push({
        key: i + 1,
        name: customerState[i].name,
        email: customerState[i].email,
        phone: customerState[i].phone || "N/A",
        address: fullAddress || "N/A",
        country: customerState[i].country || "N/A",
        // Store original customer data for export
        originalData: customerState[i],
      });
    }
  }

  // Excel Export Function
  const exportToExcel = () => {
    if (data.length === 0) {
      message.warning("No customers to export.");
      return;
    }

    setExportLoading(true);
    try {
      // Prepare data for export
      const exportData = [];

      // Add header row
      exportData.push([
        "S.No",
        "Name",
        "Email",
        "Phone",
        "Address",
        "City",
        "State",
        "Postal Code",
        "Country",
        "Full Address"
      ]);

      // Process each customer
      data.forEach((customer, index) => {
        const originalData = customer.originalData || {};

        // Format full address
        const fullAddress = [
          originalData.address,
          originalData.city,
          originalData.state,
          originalData.postal_code,
          originalData.country,
        ]
          .filter(Boolean)
          .join(", ");

        // Add customer row
        exportData.push([
          index + 1,
          customer.name || "N/A",
          customer.email || "N/A",
          customer.phone || "N/A",
          originalData.address || "N/A",
          originalData.city || "N/A",
          originalData.state || "N/A",
          originalData.postal_code || "N/A",
          customer.country || "N/A",
          fullAddress || "N/A"
        ]);
      });

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(exportData);

      // Set column widths for better readability
      const colWidths = [
        { wch: 8 },   // S.No
        { wch: 25 },  // Name
        { wch: 30 },  // Email
        { wch: 15 },  // Phone
        { wch: 30 },  // Address
        { wch: 20 },  // City
        { wch: 20 },  // State
        { wch: 12 },  // Postal Code
        { wch: 15 },  // Country
        { wch: 50 }   // Full Address
      ];
      ws['!cols'] = colWidths;

      // Freeze header row for better navigation
      ws['!freeze'] = { x: 0, y: 1 };

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Customers");

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `Customers_Export_${timestamp}.xlsx`;

      // Write file
      XLSX.writeFile(wb, filename);

      message.success(`Successfully exported ${data.length} customer(s) to Excel.`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      message.error("Failed to export customers to Excel. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  // CSV Export Function
  const exportToCSV = () => {
    if (data.length === 0) {
      message.warning("No customers to export.");
      return;
    }

    try {
      // Prepare CSV data
      let csvContent = "S.No,Name,Email,Phone,Address,City,State,Postal Code,Country,Full Address\n";

      data.forEach((customer, index) => {
        const originalData = customer.originalData || {};

        // Format full address
        const fullAddress = [
          originalData.address,
          originalData.city,
          originalData.state,
          originalData.postal_code,
          originalData.country,
        ]
          .filter(Boolean)
          .join(", ");

        const row = [
          index + 1,
          `"${(customer.name || "N/A").replace(/"/g, '""')}"`,
          customer.email || "N/A",
          customer.phone || "N/A",
          `"${((originalData.address || "N/A").replace(/"/g, '""'))}"`,
          originalData.city || "N/A",
          originalData.state || "N/A",
          originalData.postal_code || "N/A",
          customer.country || "N/A",
          `"${(fullAddress || "N/A").replace(/"/g, '""')}"`
        ].join(",");

        csvContent += row + "\n";
      });

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.setAttribute("download", `Customers_Export_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Successfully exported ${data.length} customer(s) to CSV.`);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      message.error("Failed to export customers to CSV. Please try again.");
    }
  };

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16
      }}>
        <h3 className="mb-0 title">Customers</h3>
        <Space>
          <Button
            type="default"
            icon={<FileExcelOutlined />}
            onClick={exportToExcel}
            disabled={data.length === 0}
            loading={exportLoading}
            style={{
              backgroundColor: '#52c41a',
              borderColor: '#52c41a',
              color: '#fff'
            }}
          >
            Export to Excel
          </Button>
          <Button
            type="default"
            onClick={exportToCSV}
            disabled={data.length === 0}
            style={{
              backgroundColor: '#1890ff',
              borderColor: '#1890ff',
              color: '#fff'
            }}
          >
            Export to CSV
          </Button>
        </Space>
      </div>
      <div>
        <Table columns={columns} dataSource={data} />
      </div>
    </div>
  );
};

const App = () => (
  <Provider store={store}>
    <Customers />
  </Provider>
);

export default App;
